import { EmployerRule, InputFormula } from '../api/employer-rules'
import { formatDuration } from './date'
import { UserSummaries } from '../api/missions'
import { SummaryData } from '../../../backend/src/services/resources/period-summaries/period-summary.types'
import type { WeekSummaryEmployerRule } from '../../../backend/src/services/resources/missions/missions.summary.service.types'
import { getDocIdAsString } from './documents'

/** Week summary / payroll row rules share this shape for totals display flags. */
type EmployerRuleWithTotalsDisplayFlags = Pick<
  WeekSummaryEmployerRule,
  'addToWorkedTotal' | 'display' | 'ruleId'
>

/**
 * Récupère la valeur courante d'une règle employeur
 * @param valuesInTime Liste des valeurs dans le temps
 * @returns La valeur courante ou undefined si aucune valeur n'est active
 */
export const getCurrentValue = (
  valuesInTime: EmployerRule['valuesInTime'],
): EmployerRule['valuesInTime'][0]['value'] | undefined =>
  valuesInTime.find(vt => !vt.effectiveTo || new Date(vt.effectiveTo) > new Date())?.value

/**
 * Employer rules whose durations are folded into `durations.total.total` before the Total column
 * in the mission summary table (same filter as totals-durations-columns-group).
 */
export function employerRulesShownBeforeTotalColumn<T extends EmployerRuleWithTotalsDisplayFlags>(
  rules: T[],
): T[] {
  return rules.filter(r => Boolean(r.addToWorkedTotal) && Boolean(r.display?.summary?.totals))
}

/**
 * Employer rules shown after the Total column in the mission summary totals section
 * (totals display on, not addToWorkedTotal).
 */
export function employerRulesShownAfterTotalColumn<T extends EmployerRuleWithTotalsDisplayFlags>(
  rules: T[],
): T[] {
  return rules.filter(r => !r.addToWorkedTotal && Boolean(r.display?.summary?.totals))
}

/**
 * For payroll (and similar UIs): rules to place before contract-diff column — same set as
 * {@link employerRulesShownBeforeTotalColumn}. Remaining rules keep their relative order.
 */
export function employerRulesShownBeforeContractDiffColumn<
  T extends EmployerRuleWithTotalsDisplayFlags,
>(allRules: T[]): { beforeContractDiff: T[]; afterContractDiff: T[] } {
  const beforeContractDiff = employerRulesShownBeforeTotalColumn(allRules)
  const beforeIds = new Set(beforeContractDiff.map(r => getDocIdAsString(r.ruleId)))
  const afterContractDiff = allRules.filter(r => !beforeIds.has(getDocIdAsString(r.ruleId)))
  return { beforeContractDiff, afterContractDiff }
}

/**
 * Extrait les valeurs des règles employeur depuis les données de résumé
 *
 * @param employerRules - Liste des règles employeur
 * @param summary - Données de résumé (semaine ou jour)
 * @param durationFormatter - Formateur optionnel pour les durées
 * @returns Objet avec les valeurs des règles indexées par ruleKey
 */
export const getRuleValuesFromSummary = (
  employerRules: UserSummaries['weeks'][0]['employerRules'],
  summary: SummaryData | SummaryData['days'][0],
  durationFormatter?: (duration: number) => string,
): { [key: string]: string | number } =>
  employerRules.reduce<{
    [key: string]: string | number
  }>((rules, employerRule) => {
    const ruleKey = `rule_${employerRule.ruleId}`

    if (employerRule.kind === 'goal') {
      rules[ruleKey] = summary?.employerData?.goals?.[getDocIdAsString(employerRule.ruleId)] || 0
    } else {
      const duration =
        summary?.employerData?.durations?.[getDocIdAsString(employerRule.ruleId)] || 0
      rules[ruleKey] = durationFormatter ? durationFormatter(duration) : formatDuration(duration)
    }
    return rules
  }, {})

/**
 * Recursively extracts all work-period-event IDs referenced by eventPath nodes
 * in an InputFormula AST.
 */
export const extractEventIdsFromFormula = (formula: InputFormula): string[] => {
  if ('op' in formula) {
    return formula.args.flatMap(extractEventIdsFromFormula)
  }
  if ('type' in formula && formula.type === 'eventPath') {
    return formula.workPeriodEventIds
  }
  return []
}

/**
 * Build a map from work-period-event ID to the list of employer rule names
 * that reference it via an eventPath formula node.
 */
export function buildEventRulesMap(employerRules: EmployerRule[]): Map<string, string[]> {
  const map = new Map<string, string[]>()
  for (const rule of employerRules) {
    const eventIds = extractEventIdsFromFormula(rule.interval.inputFormula)
    for (const id of eventIds) {
      const existing = map.get(id) || []
      existing.push(rule.shortName || rule.name)
      map.set(id, existing)
    }
  }
  return map
}
